import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, API } from "@/lib/api";
import Seo from "@/components/Seo";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { FileText, Download } from "lucide-react";

const CATS = ["PRODUCT_IDENTITY", "MATERIALS", "ORIGIN", "REPAIR_CARE", "END_OF_LIFE", "FOOTPRINT", "CERTIFICATIONS"];

export default function SectorReport() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const [report, setReport] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setReport(null); setNotFound(false);
    http.get(`/sector-reports/${slug}`).then((r) => setReport(r.data.report)).catch(() => setNotFound(true));
  }, [slug]);

  const reportJsonLd = useMemo(() => (report ? {
    "@context": "https://schema.org",
    "@type": "Report",
    headline: `${report.sector_code} ${report.quarter} sector report`,
    datePublished: report.approved_at || report.generated_at,
    author: { "@type": "Organization", name: "BrandPassport" },
  } : null), [report]);

  if (notFound) return <div className="bp-container py-24"><h1 className="font-serif text-4xl">Report not found</h1></div>;
  if (!report) return <div className="bp-container py-24" />;

  const total = report.aggregates.total_indexed;
  const pdfUrl = `${API}/sector-reports/${slug}.pdf`;
  const body = pickLocalized(report.body, locale);

  return (
    <>
      <Seo
        title={`${report.sector_code} — ${report.quarter} sector report`}
        description={`Factual quarterly sector report for ${report.sector_code} on BrandPassport (${report.quarter}). Counts, not rankings.`}
        canonicalPath={`/reports/${slug}`}
        locale={locale}
        ogImage={`${API}/data/aggregates.csv`}
        jsonLd={reportJsonLd}
      />
      <section className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-12">
          <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Sector report · {report.quarter}</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none" data-testid="sector-report-title">
            {report.sector_code === "FASHION" ? "Fashion" : report.sector_code === "FURNITURE" ? "Furniture" : report.sector_code} — {report.quarter}
          </h1>
          <p className="mt-4 text-sm font-mono" style={{ color: "var(--bp-text-secondary)" }}>
            Factual counts only — brands are not compared or ordered.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-black text-sm" data-testid="report-pdf-link"><Download size={14} /> Download PDF</a>
            <Link to="/data" className="inline-flex items-center gap-2 px-4 py-2 border text-sm" style={{ borderColor: "var(--bp-border)" }}><FileText size={14} /> Data & methodology</Link>
          </div>
        </div>
      </section>

      <div className="bp-container py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--bp-text-secondary)" }} data-testid="report-body">
          {body.split("\n\n").map((p, i) => <p key={`p-${i}-${p.slice(0, 20)}`}>{p}</p>)}
        </div>
        <aside className="p-6 bg-white border h-fit" style={{ borderColor: "var(--bp-border)" }}>
          <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Category counts</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--bp-border)" }}>
                <th className="py-2 text-xs uppercase tracking-widest">Category</th>
                <th className="py-2 text-xs uppercase tracking-widest text-right">Found</th>
                <th className="py-2 text-xs uppercase tracking-widest text-right">Partial</th>
                <th className="py-2 text-xs uppercase tracking-widest text-right">Not found</th>
              </tr>
            </thead>
            <tbody>
              {CATS.map((cat) => {
                const c = report.aggregates.counts[cat] || { FOUND: 0, PARTIAL: 0, NOT_FOUND: 0 };
                return (
                  <tr key={cat} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`report-count-${cat}`}>
                    <td className="py-2">{t.checklist_categories[cat]}</td>
                    <td className="py-2 text-right font-mono">{c.FOUND}</td>
                    <td className="py-2 text-right font-mono">{c.PARTIAL}</td>
                    <td className="py-2 text-right font-mono">{c.NOT_FOUND}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Total brands indexed in sector: {total}</div>
        </aside>
      </div>
    </>
  );
}
